import React, { useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';

export interface IInvestorsContactCTACallout {
	/**
	 * Callout Button Text
	 */
	calloutButtonText?: string;
	/**
	 * Callout Button Style
	 */
	calloutButtonColor?: 'Primary' | 'Light';
	/**
	 * ID of the Gravity Form to embed in Modal
	 */
	gravityFormId?: number;
	/**
	 * You can use this to put rawHTML, for example from a third party service, which will be used instead of a Gravity Form
	 *
	 * @control textarea
	 */
	rawHtml?: string;
}

export interface IInvestorsContactCTAAddress {
	/**
	 * Contact CTA Address Addressee
	 */
	addressee?: string;
	/**
	 * Contact CTA Address Civic Address
	 */
	civicAddress?: string;
	/**
	 * Contact CTA Address Municipality, Province and Postal Code
	 */
	municipalityProvincePostalCode?: string;
	/**
	 * Contact CTA Address Phone Number
	 */
	phoneNumber?: string;
	/**
	 * Contact CTA Address Email
	 */
	email?: string;
}

export interface IInvestorsContactCTAProps extends IBaseElementProps {
	children;
	/**
	 * CTA Contact Image
	 */
	contactCTAImage: IImage;
	/**
	 * CTA Contact Heading
	 * @control richtext
	 */
	contactCTAHeading?: string;
	/**
	 * CTA Contact Sub Heading
	 * @control richtext
	 */
	contactCTASubHeading?: string;
	contactCTACallouts: IInvestorsContactCTACallout[];
	/**
	 * CTA Contact Additional Link - shown below the callout buttons.
	 */
	contactCTAAdditonalLink?: ILink;
	contactCTAAddress: IInvestorsContactCTAAddress;
	contactCTATransferAgentAddress: IInvestorsContactCTAAddress;
	/**
	 * CTA Contact Auditor Heading
	 */
	contactCTAAuditorHeading?: string;
	/**
	 * CTA Contact Auditor
	 */
	contactCTAAuditor?: ILink;
}

/**
 * Calian Investors Contact CTA Component
 * @icon welcome-widgets-menus
 * @block
 */
const InvestorsContactCTA: React.FC<IInvestorsContactCTAProps> = ({
	id,
	className,
	classNames = [],
	children,
	contactCTAImage,
	contactCTAHeading,
	contactCTASubHeading,
	contactCTACallouts,
	contactCTAAdditonalLink,
	contactCTAAddress,
	contactCTATransferAgentAddress,
	contactCTAAuditor,
	contactCTAAuditorHeading,
}) => {
	const [showModal, setShowModal] = useState(null);

	const handleClick = (modalNumber) => {
		setShowModal(modalNumber);
	};

	return (
		<div
			id={id}
			className={
				className || cn('calian-investors-contact-cta', ...classNames)
			}
		>
			<div className="calian-investors-contact-cta__container container-fluid">
				<div className="row">
					<div className="calian-investors-contact-cta__left col-12 col-md-6">
						<div className="container-fluid">
							<div className="row">
								{contactCTAImage && (
									<div className="calian-investors-contact-cta__left--image col-12 col-xl-5">
										<Image
											{...contactCTAImage}
											className="calian-investors-contact-cta--image img-fluid"
										/>
									</div>
								)}
								<div className="calian-investors-contact-cta__left--content col-12 col-xl-7">
									{contactCTAHeading && (
										<h4
											className="calian-investors-contact-cta--heading"
											dangerouslySetInnerHTML={{
												__html: contactCTAHeading,
											}}
										/>
									)}
									{contactCTASubHeading && (
										<p
											className="calian-investors-contact-cta--sub-heading"
											dangerouslySetInnerHTML={{
												__html: contactCTASubHeading,
											}}
										/>
									)}
									<div className="calian-investors-contact-cta__left-callouts">
										{(contactCTACallouts || []).map(
											(callout, index) => {
												return (
													<Button
														key={index}
														className={`btn btn-sm btn-${kebabCase(
															callout.calloutButtonColor,
														)}`}
														onClick={() =>
															handleClick(index)
														}
													>
														{
															callout.calloutButtonText
														}
													</Button>
												);
											},
										)}
										{contactCTAAdditonalLink?.label && (
											<Button
												className="calian-investors-contact-cta__additional-link"
												{...contactCTAAdditonalLink}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="calian-investors-contact-cta__right col-12 col-md-6">
						<div className="calian-investors-contact-cta__right--content">
							{contactCTAAddress && (
								<address className="calian-investors-contact-cta__address">
									{contactCTAAddress.addressee && (
										<p className="calian-investors-contact-cta__address--addressee">
											{contactCTAAddress.addressee}
										</p>
									)}
									{contactCTAAddress.civicAddress && (
										<p className="calian-investors-contact-cta__address--civic-address">
											{contactCTAAddress.civicAddress}
										</p>
									)}
									{contactCTAAddress.municipalityProvincePostalCode && (
										<p className="calian-investors-contact-cta__address--additional-address-details">
											{
												contactCTAAddress.municipalityProvincePostalCode
											}
										</p>
									)}
									<p className="calian-investors-contact-cta__address--phone-email">
										{contactCTAAddress.phoneNumber && (
											<a
												className="calian-investors-contact-cta__address--phone"
												href={
													'tel:+' +
													contactCTAAddress.phoneNumber +
													''
												}
											>
												{contactCTAAddress.phoneNumber}
											</a>
										)}
										{contactCTAAddress.email && (
											<a
												className="calian-investors-contact-cta__address--email"
												href={
													'mailto:' +
													contactCTAAddress.email +
													''
												}
											>
												{contactCTAAddress.email}
											</a>
										)}
									</p>
								</address>
							)}
							{contactCTATransferAgentAddress && (
								<address className="calian-investors-contact-cta__address">
									{contactCTATransferAgentAddress.addressee && (
										<p className="calian-investors-contact-cta__address--addressee">
											{
												contactCTATransferAgentAddress.addressee
											}
										</p>
									)}
									{contactCTATransferAgentAddress.civicAddress && (
										<p className="calian-investors-contact-cta__address--civic-address">
											{
												contactCTATransferAgentAddress.civicAddress
											}
										</p>
									)}
									{contactCTATransferAgentAddress.municipalityProvincePostalCode && (
										<p className="calian-investors-contact-cta__address--additional-address-details">
											{
												contactCTATransferAgentAddress.municipalityProvincePostalCode
											}
										</p>
									)}
									<p className="calian-investors-contact-cta__address--phone-email">
										{contactCTATransferAgentAddress.phoneNumber && (
											<a
												className="calian-investors-contact-cta__address--phone"
												href={
													'tel:+' +
													contactCTATransferAgentAddress.phoneNumber +
													''
												}
											>
												{
													contactCTATransferAgentAddress.phoneNumber
												}
											</a>
										)}
										{contactCTATransferAgentAddress.email && (
											<a
												className="calian-investors-contact-cta__address--email"
												href={
													'mailto:' +
													contactCTATransferAgentAddress.email +
													''
												}
											>
												{
													contactCTATransferAgentAddress.email
												}
											</a>
										)}
									</p>
								</address>
							)}
							{contactCTAAuditor?.label && (
								<div className="calian-investors-contact-cta__right--auditors">
									<p className="calian-investors-contact-cta__right--auditors-title">
										{contactCTAAuditorHeading}
									</p>
									{contactCTAAuditor?.label && (
										<Button
											className="calian-investors-contact-cta__right--auditors-title"
											{...contactCTAAuditor}
										/>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{showModal !== null && (
				<Modal
					onCancel={() => setShowModal(null)}
					modalClassNames={[
						'calian-investors-contact-cta__modal modal-dialog modal-dialog-centered',
					]}
					headerClassNames={['modal-header']}
					bodyClassNames={['modal-body']}
					trapFocus
				>
					<div className="calian-investors-contact-cta__modal--container container-fluid">
						<div className="row">
							<div className="col-12">
								<div className="calian-investors-contact-cta__modal--form">
									{children && children[showModal] ? (
										children[showModal]
									) : (
										<>
											Front end will show Form{' '}
											{
												contactCTACallouts[showModal]
													.gravityFormId
											}
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default InvestorsContactCTA;
